import React, { useState, useMemo, useEffect } from 'react'
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import {
  UserGroupIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  CreditCardIcon,
  DevicePhoneMobileIcon,
  ChartBarIcon,
  CalendarIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { useGetTrialAnalyticsQuery, TrialAnalyticsData, ActionStatRow } from '../store/api/trialAnalyticsApi'

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || isNaN(seconds)) return '—'
  const t = Math.max(0, Math.floor(seconds))
  const h = Math.floor(t / 3600)
  const m = Math.floor((t % 3600) / 60)
  const s = t % 60
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

function formatLabel(action: string, label: string): string {
  return label !== action ? label : action.replace(/_/g, ' ')
}

const TODAY = new Date().toISOString().slice(0, 10)

const DEVICE_COLORS: Record<string, string> = {
  desktop_mac: '#6366f1',
  desktop_windows: '#3b82f6',
  ios: '#10b981',
  android: '#f59e0b',
  unknown: '#9ca3af',
}

const RATING_COLORS: Record<string, string> = {
  yes: '#10b981',
  no: '#ef4444',
  maybe: '#f59e0b',
  no_rating: '#9ca3af',
}

// ─── Sub-components ──────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string
  value: string | number
  sub?: string
  icon: React.ElementType
  color: string
}

function KpiCard({ label, value, sub, icon: Icon, color }: KpiCardProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex items-start gap-4">
      <div className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5 leading-none">{value}</p>
        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
      </div>
    </div>
  )
}

interface SectionProps {
  title: string
  subtitle?: string
  children: React.ReactNode
}

function Section({ title, subtitle, children }: SectionProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  )
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="font-medium text-gray-800 mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color ?? p.fill }} className="text-xs">
          {p.name}: <strong>{p.value?.toLocaleString()}</strong>
        </p>
      ))}
    </div>
  )
}

// ─── Step table ───────────────────────────────────────────────────────────────

interface StepTableProps {
  rows: { action: string; label: string; session_count: number; avg_seconds_from_start: number | null }[]
  maxCount: number
  showTime?: boolean
}

function StepTable({ rows, maxCount, showTime = true }: StepTableProps) {
  if (rows.length === 0) return <p className="text-sm text-gray-400 text-center py-6">No data available.</p>
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-100">
          <th className="text-left pb-2 text-xs text-gray-500 font-medium uppercase">Step</th>
          <th className="text-right pb-2 text-xs text-gray-500 font-medium uppercase w-24">Sessions</th>
          {showTime && (
            <th className="text-right pb-2 text-xs text-gray-500 font-medium uppercase w-28">Avg from start</th>
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {rows.map((r, i) => {
          const pct = maxCount > 0 ? Math.round((r.session_count / maxCount) * 100) : 0
          return (
            <tr key={r.action} className="hover:bg-gray-50">
              <td className="py-2 pr-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                  <div className="flex-1">
                    <p className="text-gray-800 font-medium leading-snug">{formatLabel(r.action, r.label)}</p>
                    <div className="mt-1 h-1.5 rounded-full bg-gray-100 w-full">
                      <div
                        className="h-1.5 rounded-full bg-indigo-400"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                </div>
              </td>
              <td className="py-2 text-right font-semibold text-gray-700 tabular-nums">
                {r.session_count.toLocaleString()}
                <span className="text-xs text-gray-400 ml-1">({pct}%)</span>
              </td>
              {showTime && (
                <td className="py-2 text-right text-gray-500 tabular-nums text-xs">
                  {formatDuration(r.avg_seconds_from_start)}
                </td>
              )}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

// ─── Action stats table ───────────────────────────────────────────────────────

function ActionStatsTable({ rows }: { rows: ActionStatRow[] }) {
  const [filter, setFilter] = useState('')
  const maxOccurrences = rows[0]?.total_occurrences ?? 1
  const filtered = filter
    ? rows.filter((r) => r.label.toLowerCase().includes(filter.toLowerCase()) || r.action.toLowerCase().includes(filter.toLowerCase()))
    : rows

  return (
    <div>
      <div className="mb-3">
        <input
          type="text"
          placeholder="Filter actions…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
        />
      </div>
      <div className="overflow-y-auto max-h-96">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="border-b border-gray-100">
              <th className="text-left pb-2 text-xs text-gray-500 font-medium uppercase">Action</th>
              <th className="text-right pb-2 text-xs text-gray-500 font-medium uppercase w-24">Total</th>
              <th className="text-right pb-2 text-xs text-gray-500 font-medium uppercase w-24">Users</th>
              <th className="text-right pb-2 text-xs text-gray-500 font-medium uppercase w-28">Avg time to next</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map((r) => {
              const pct = maxOccurrences > 0 ? Math.round((r.total_occurrences / maxOccurrences) * 100) : 0
              return (
                <tr key={r.action} className="hover:bg-gray-50">
                  <td className="py-2 pr-4">
                    <p className="text-gray-800 font-medium leading-snug">{formatLabel(r.action, r.label)}</p>
                    <div className="mt-1 h-1 rounded-full bg-gray-100 w-full">
                      <div className="h-1 rounded-full bg-indigo-300" style={{ width: `${pct}%` }} />
                    </div>
                  </td>
                  <td className="py-2 text-right font-semibold text-gray-700 tabular-nums">
                    {r.total_occurrences.toLocaleString()}
                  </td>
                  <td className="py-2 text-right text-gray-500 tabular-nums">
                    {r.unique_users.toLocaleString()}
                  </td>
                  <td className="py-2 text-right text-gray-500 tabular-nums text-xs">
                    {formatDuration(r.avg_seconds_to_next)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">No matching actions.</p>
        )}
      </div>
    </div>
  )
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
}

// ─── Main page ────────────────────────────────────────────────────────────────

const TrialAnalytics: React.FC = () => {
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [appliedFrom, setAppliedFrom] = useState('')
  const [appliedTo, setAppliedTo] = useState('')
  const [hasSeededDates, setHasSeededDates] = useState(false)

  const queryParams = useMemo(
    () => ({ dateFrom: appliedFrom || undefined, dateTo: appliedTo || undefined }),
    [appliedFrom, appliedTo]
  )

  const { data: resp, isLoading, isFetching, isError, refetch } = useGetTrialAnalyticsQuery(queryParams)
  const d: TrialAnalyticsData | undefined = resp?.data

  // Once data loads for the first time, seed the date inputs with the actual data range
  useEffect(() => {
    if (!hasSeededDates && d?.dataRange?.firstDate) {
      const first = d.dataRange.firstDate
      const last = d.dataRange.lastDate ?? TODAY
      setDateFrom(first)
      setDateTo(last)
      setHasSeededDates(true)
    }
  }, [d, hasSeededDates])

  function applyFilter() {
    setAppliedFrom(dateFrom)
    setAppliedTo(dateTo)
  }

  function clearFilter() {
    const first = d?.dataRange?.firstDate ?? ''
    const last = d?.dataRange?.lastDate ?? TODAY
    setDateFrom(first)
    setDateTo(last)
    setAppliedFrom('')
    setAppliedTo('')
  }

  const loading = isLoading || isFetching

  // Build hourly chart with all 24 hours filled in
  const fullHourly = useMemo(() => {
    if (!d) return []
    const map = new Map(d.hourlyTraffic.map((r) => [r.hour, r.visitor_count]))
    return Array.from({ length: 24 }, (_, h) => ({
      hour: `${String(h).padStart(2, '0')}:00`,
      visitors: map.get(h) ?? 0,
    }))
  }, [d])

  const totalFromHourly = useMemo(
    () => fullHourly.reduce((sum, r) => sum + r.visitors, 0),
    [fullHourly]
  )

  const maxFirstStep = d?.firstSteps[0]?.session_count ?? 1
  const maxLastStep = d?.lastSteps[0]?.session_count ?? 1

  // Pie data for devices
  const devicePieData = useMemo(
    () =>
      (d?.deviceBreakdown ?? []).map((r) => ({
        name: r.device.replace(/_/g, ' '),
        value: r.count,
        fill: DEVICE_COLORS[r.device] ?? '#9ca3af',
      })),
    [d]
  )

  // Pie data for feedback ratings
  const ratingPieData = useMemo(
    () =>
      (d?.feedbackRatings ?? []).map((r) => ({
        name: r.rating === 'no_rating' ? 'No rating' : r.rating.charAt(0).toUpperCase() + r.rating.slice(1),
        value: r.count,
        fill: RATING_COLORS[r.rating] ?? '#9ca3af',
      })),
    [d]
  )

  const totalFeedback = useMemo(() => ratingPieData.reduce((s, r) => s + r.value, 0), [ratingPieData])

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto space-y-6">
      {/* ── Header ── */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trial Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Deep-dive into how visitors experience the trial flow
          </p>
        </div>

        {/* Date filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <CalendarIcon className="w-4 h-4 text-gray-400 hidden sm:block" />
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={dateFrom}
              max={TODAY}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              placeholder="From"
            />
            <span className="text-gray-400 text-sm">→</span>
            <input
              type="date"
              value={dateTo}
              max={TODAY}
              onChange={(e) => setDateTo(e.target.value)}
              className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              placeholder="To"
            />
          </div>
          <button
            onClick={applyFilter}
            className="px-3.5 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Apply
          </button>
          {(appliedFrom || appliedTo) && (
            <button
              onClick={clearFilter}
              className="px-3 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear
            </button>
          )}
          <button
            onClick={() => refetch()}
            disabled={loading}
            title="Refresh"
            className="p-1.5 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition-colors"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        {d?.dataRange?.firstDate && (
          <span className="text-xs text-gray-500 bg-gray-100 border border-gray-200 rounded-lg px-3 py-1.5">
            Data available from{' '}
            <strong className="text-gray-700">{d.dataRange.firstDate}</strong>
            {' '}to{' '}
            <strong className="text-gray-700">{d.dataRange.lastDate ?? TODAY}</strong>
          </span>
        )}
        {(appliedFrom || appliedTo) && (
          <span className="text-xs text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-1.5">
            Filtered: <strong>{appliedFrom || 'start'}</strong> → <strong>{appliedTo || 'today'}</strong>
          </span>
        )}
        {!appliedFrom && !appliedTo && !isLoading && (
          <span className="text-xs text-gray-400">No filter applied — showing all-time data</span>
        )}
      </div>

      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          Failed to load analytics. Check the server connection and try refreshing.
        </div>
      )}

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24" />)
        ) : d ? (
          <>
            <KpiCard
              label="Total Visitors"
              value={d.summary.totalVisitors.toLocaleString()}
              sub="Unique sessions started"
              icon={UserGroupIcon}
              color="bg-indigo-500"
            />
            <KpiCard
              label="Registered"
              value={d.summary.registeredUsers.toLocaleString()}
              sub={`${d.summary.registrationRate}% conversion`}
              icon={ArrowTrendingUpIcon}
              color="bg-emerald-500"
            />
            <KpiCard
              label="Projects Created"
              value={d.summary.projectsCreated.toLocaleString()}
              sub={`${d.summary.projectCreationRate}% of visitors`}
              icon={ChartBarIcon}
              color="bg-blue-500"
            />
            <KpiCard
              label="Credits Purchased"
              value={d.summary.creditsPurchasedUsers.toLocaleString()}
              sub={`${d.summary.creditConversionRate}% paid conversion`}
              icon={CreditCardIcon}
              color="bg-amber-500"
            />
            <KpiCard
              label="Avg Duration"
              value={formatDuration(d.summary.avgDurationSeconds)}
              sub={`Median: ${formatDuration(d.summary.medianDurationSeconds)}`}
              icon={ClockIcon}
              color="bg-violet-500"
            />
            <KpiCard
              label="Avg Actions/Session"
              value={d.summary.avgActionsPerSession}
              sub={`${d.summary.totalActivityEvents.toLocaleString()} total events`}
              icon={DevicePhoneMobileIcon}
              color="bg-rose-500"
            />
          </>
        ) : null}
      </div>

      {/* ── Daily Trend + Hourly Traffic ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Daily Visitors" subtitle="Unique sessions per day">
          {loading ? (
            <Skeleton className="h-52" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={d?.dailyTrend.map((r) => ({ date: r.date.slice(5), visitors: r.visitor_count })) ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} width={32} />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="visitors"
                  name="Visitors"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Section>

        <Section title="Traffic by Hour of Day" subtitle="When visitors most commonly start their session (UTC)">
          {loading ? (
            <Skeleton className="h-52" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={fullHourly} barSize={14}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
                <YAxis tick={{ fontSize: 11 }} width={32} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="visitors" name="Visitors" fill="#6366f1" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
          {!loading && totalFromHourly > 0 && (() => {
            const peak = fullHourly.reduce((best, r) => r.visitors > best.visitors ? r : best, fullHourly[0])
            return (
              <p className="text-xs text-gray-500 mt-2">
                Peak hour: <strong className="text-gray-700">{peak.hour}</strong> with{' '}
                <strong className="text-gray-700">{peak.visitors}</strong> visitors
              </p>
            )
          })()}
        </Section>
      </div>

      {/* ── Weekday Traffic ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Section title="Traffic by Day of Week" subtitle="Which days drive the most trial sessions">
          {loading ? (
            <Skeleton className="h-52" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={(() => {
                  const all = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
                  const map = new Map((d?.weekdayTraffic ?? []).map((r) => [r.weekday_name, r.visitor_count]))
                  return all.map((name) => ({ name, visitors: map.get(name) ?? 0 }))
                })()}
                barSize={28}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} width={32} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="visitors" name="Visitors" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>

        {/* Device Breakdown */}
        <Section title="Device Breakdown" subtitle="Device type at session start">
          {loading ? (
            <Skeleton className="h-52" />
          ) : devicePieData.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-12">No device data.</p>
          ) : (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="55%" height={200}>
                <PieChart>
                  <Pie data={devicePieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={2}>
                    {devicePieData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val: number, name: string) => [val.toLocaleString(), name]} />
                </PieChart>
              </ResponsiveContainer>
              <ul className="flex-1 space-y-2 text-sm">
                {devicePieData.map((r) => {
                  const total = devicePieData.reduce((s, x) => s + x.value, 0)
                  const pct = total > 0 ? Math.round((r.value / total) * 100) : 0
                  return (
                    <li key={r.name} className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: r.fill }} />
                      <span className="text-gray-700 capitalize flex-1 truncate">{r.name}</span>
                      <span className="font-semibold text-gray-900 tabular-nums">{pct}%</span>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </Section>

        {/* Feedback Ratings */}
        <Section title="Demo Feedback Ratings" subtitle={`${totalFeedback} total feedback submissions`}>
          {loading ? (
            <Skeleton className="h-52" />
          ) : ratingPieData.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-12">No feedback data.</p>
          ) : (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="55%" height={200}>
                <PieChart>
                  <Pie data={ratingPieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={2}>
                    {ratingPieData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val: number, name: string) => [val.toLocaleString(), name]} />
                </PieChart>
              </ResponsiveContainer>
              <ul className="flex-1 space-y-2 text-sm">
                {ratingPieData.map((r) => {
                  const pct = totalFeedback > 0 ? Math.round((r.value / totalFeedback) * 100) : 0
                  return (
                    <li key={r.name} className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: r.fill }} />
                      <span className="text-gray-700 capitalize flex-1">{r.name}</span>
                      <span className="font-semibold text-gray-900 tabular-nums">
                        {r.value} <span className="text-gray-400 font-normal text-xs">({pct}%)</span>
                      </span>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </Section>
      </div>

      {/* ── Duration Distribution ── */}
      <Section title="Session Duration Distribution" subtitle="How long users spend in the trial flow">
        {loading ? (
          <Skeleton className="h-44" />
        ) : (d?.durationDistribution ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No duration data.</p>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart
              data={(d?.durationDistribution ?? []).map((r) => ({ bucket: r.bucket, users: r.user_count }))}
              barSize={48}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} width={36} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="users" name="Users" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Section>

      {/* ── Source / Origin ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Traffic Sources" subtitle="Where visitors come from (referral source)">
          {loading ? (
            <Skeleton className="h-56" />
          ) : (d?.sourceBreakdown ?? []).length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">No source data.</p>
          ) : (
            <div className="space-y-2">
              {(d?.sourceBreakdown ?? []).map((r, i) => {
                const max = d!.sourceBreakdown[0]?.count ?? 1
                const pct = Math.round((r.count / max) * 100)
                return (
                  <div key={r.source} className="flex items-center gap-3 text-sm">
                    <span className="text-xs font-bold text-gray-400 w-4 text-right">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-gray-700 truncate max-w-[70%]" title={r.source}>{r.source}</span>
                        <span className="font-semibold text-gray-900 tabular-nums ml-2">{r.count}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-gray-100">
                        <div className="h-1.5 rounded-full bg-blue-400" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Section>

        <Section title="Entry Points (Origin)" subtitle="Which Motionode page they came from">
          {loading ? (
            <Skeleton className="h-56" />
          ) : (d?.originBreakdown ?? []).length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">No origin data.</p>
          ) : (
            <div className="space-y-2">
              {(d?.originBreakdown ?? []).map((r, i) => {
                const max = d!.originBreakdown[0]?.count ?? 1
                const pct = Math.round((r.count / max) * 100)
                return (
                  <div key={r.origin} className="flex items-center gap-3 text-sm">
                    <span className="text-xs font-bold text-gray-400 w-4 text-right">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-gray-700 truncate max-w-[70%]" title={r.origin}>{r.origin}</span>
                        <span className="font-semibold text-gray-900 tabular-nums ml-2">{r.count}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-gray-100">
                        <div className="h-1.5 rounded-full bg-emerald-400" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Section>
      </div>

      {/* ── Location Breakdown ── */}
      <Section title="Visitor Locations" subtitle="Top countries by unique trial visitors">
        {loading ? (
          <Skeleton className="h-44" />
        ) : (d?.locationBreakdown ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No location data.</p>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart
              data={(d?.locationBreakdown ?? []).map((r) => ({ country: r.country, visitors: r.count }))}
              layout="vertical"
              margin={{ left: 20, right: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="country" tick={{ fontSize: 11 }} width={60} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="visitors" name="Visitors" fill="#14b8a6" radius={[0, 4, 4, 0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Section>

      {/* ── First Steps / Last Steps ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section
          title="First 5 Actions in a Session"
          subtitle="Most common actions users take in the first 5 steps — with avg time from session start"
        >
          {loading ? (
            <Skeleton className="h-56" />
          ) : (
            <StepTable rows={d?.firstSteps ?? []} maxCount={maxFirstStep} showTime />
          )}
        </Section>

        <Section
          title="Last 3 Actions Before Leaving"
          subtitle="Most common actions users do right before their session ends — indicates drop-off points"
        >
          {loading ? (
            <Skeleton className="h-56" />
          ) : (
            <StepTable rows={d?.lastSteps ?? []} maxCount={maxLastStep} showTime />
          )}
        </Section>
      </div>

      {/* ── Action Stats full table ── */}
      <Section
        title="All Action Stats"
        subtitle="Every tracked action — frequency, unique users, and average time until the next action"
      >
        {loading ? (
          <Skeleton className="h-72" />
        ) : (
          <ActionStatsTable rows={d?.actionStats ?? []} />
        )}
      </Section>

      {/* ── Top Actions bar chart ── */}
      <Section title="Top 20 Most-Performed Actions" subtitle="By total occurrence count">
        {loading ? (
          <Skeleton className="h-72" />
        ) : (d?.actionStats ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No action data.</p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={(d?.actionStats ?? []).slice(0, 20).map((r) => ({
                name: r.label.length > 32 ? r.label.slice(0, 32) + '…' : r.label,
                occurrences: r.total_occurrences,
                users: r.unique_users,
              }))}
              layout="vertical"
              margin={{ left: 8, right: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={230} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="occurrences" name="Total occurrences" fill="#6366f1" barSize={12} radius={[0, 3, 3, 0]} />
              <Bar dataKey="users" name="Unique users" fill="#a5b4fc" barSize={12} radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Section>
    </div>
  )
}

export default TrialAnalytics
